<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

interface Props {
  modelValue?: boolean
  width?: string
  mobileHeight?: string
  title?: string
  showClose?: boolean
  closeOnOverlay?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  width: '400px',
  mobileHeight: '40dvh',
  showClose: true,
  closeOnOverlay: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
  open: []
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)

    if (value) {
      emit('open')
    } else {
      emit('close')
    }
  },
})

const close = () => {
  isOpen.value = false
}

const handleOverlayClick = () => {
  if (props.closeOnOverlay) {
    close()
  }
}

const scrollContainer = ref<HTMLElement | null>(null)
const thumbHeight = ref(0)
const thumbTop = ref(0)
const hasScroll = ref(false)

const updateThumb = () => {
  const el = scrollContainer.value
  if (!el) return

  const { scrollTop, scrollHeight, clientHeight } = el

  if (scrollHeight <= clientHeight) {
    hasScroll.value = false
    return
  }

  hasScroll.value = true
  thumbHeight.value = Math.max((clientHeight / scrollHeight) * clientHeight, 24)
  thumbTop.value = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight.value)
}

const lockBodyScroll = () => {
  document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  document.body.style.overflow = ''
}

watch(
  isOpen,
  async (value) => {
    if (value) {
      lockBodyScroll()

      await nextTick()
      updateThumb()
    } else {
      unlockBodyScroll()
    }
  },
  { immediate: true }
)

onMounted(() => {
  updateThumb()
  window.addEventListener('resize', updateThumb)
})

onUnmounted(() => {
  unlockBodyScroll()
  window.removeEventListener('resize', updateThumb)
})
</script>

<template>
    <Teleport to="body">
        <Transition name="offcanvas">
            <div v-if="isOpen" class="fixed inset-0 z-[9999]">
                <!-- Overlay -->
                <div class="absolute inset-0 bg-[rgba(46,67,105,.502)] backdrop-blur-[1px] cursor-pointer"
                    @click.stop="handleOverlayClick" />

                <!-- Panel -->
                <aside @click.stop
                    class="absolute inset-x-0 bottom-0 flex h-[var(--offcanvas-mobile-height)] max-h-none w-full flex-col rounded-t-2xl bg-white shadow-xl sm:inset-y-0 sm:left-0 sm:right-auto sm:bottom-auto sm:h-dvh sm:max-h-none sm:w-[var(--offcanvas-width)] sm:rounded-tr-xl sm:rounded-br-xl sm:rounded-tl-none sm:rounded-bl-none"
                    :style="{ '--offcanvas-width': width, '--offcanvas-mobile-height': mobileHeight }" role="dialog"
                    aria-modal="true">
                    <!-- Header -->
                    <header v-if="$slots.header || title || showClose"
                        class="flex shrink-0 items-start px-4 sm:px-8 py-4 sm:pt-8 sm:pb-6"
                        :class="$slots.header ? ' justify-between' : ' justify-end'">
                        <slot name="header">
                            <h2 v-if="title" class="text-base font-semibold text-gray-900">
                                {{ title }}
                            </h2>
                        </slot>

                        <button v-if="showClose" type="button" class="cursor-pointer text-2xl text-[#1A1A1A]"
                            aria-label="Close panel" @click="close">
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.668 10.667L21.3346 21.3337" stroke="#1A1A1A" stroke-width="1.5"
                                    stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M21.3346 10.667L10.668 21.3337" stroke="#1A1A1A" stroke-width="1.5"
                                    stroke-linecap="round" stroke-linejoin="round" />
                            </svg>

                        </button>
                    </header>

                    <!-- Body -->
                    <div class="relative min-h-0 flex-1 group">
                        <div ref="scrollContainer" class="no-native-scrollbar h-full overflow-y-auto"
                            @scroll="updateThumb">
                            <slot />
                        </div>

                        <div v-if="hasScroll" class="custom-thumb"
                            :style="{ height: thumbHeight + 'px', transform: `translateY(${thumbTop}px)` }" />
                    </div>

                    <!-- Footer -->
                    <footer v-if="$slots.footer" class="shrink-0 p-4 sm:px-8 sm:pb-8 sm:pt-6">
                        <slot name="footer" />
                    </footer>
                </aside>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.no-native-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.no-native-scrollbar::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
}

.custom-thumb {
    position: absolute;
    top: 0;
    inset-inline-end: 2px;
    width: 3px;
    border-radius: 999px;
    background-color: rgba(27, 50, 90, 0.25);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.group:hover .custom-thumb {
    opacity: 1;
}

.offcanvas-enter-active,
.offcanvas-leave-active {
    transition: opacity 250ms ease;
}

.offcanvas-enter-active aside,
.offcanvas-leave-active aside {
    transition: transform 300ms ease;
}

.offcanvas-enter-from,
.offcanvas-leave-to {
    opacity: 0;
}

.offcanvas-enter-from aside,
.offcanvas-leave-to aside {
    transform: translateX(-100%);
}

.offcanvas-enter-to aside,
.offcanvas-leave-from aside {
    transform: translateX(0);
}

@media (max-width: 639px) {

    .offcanvas-enter-from aside,
    .offcanvas-leave-to aside {
        transform: translateY(100%);
    }

    .offcanvas-enter-to aside,
    .offcanvas-leave-from aside {
        transform: translateY(0);
    }
}
</style>
